import React, { useContext, useEffect, useState } from 'react';
import {
  AlertBox,
  AlertBoxDismissButton,
  Box,
  Column,
  GridContainer,
  H2,
  Row,
  Typography,
  useScreenClass,
} from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { useFeature } from '@vp/core-ui-components';
import { OrderDetailsContext } from '../contexts/OrderDetailsContext';
import DetailsOrderUxModWrapper from '../../uxMods/orderDetails/DetailsOrderUxModWrapper';
import OrderCondensedSummary from './OrderCondensedSummary';
import { OrderSummary } from './orderPricing/OrderSummary';
import { LineItemList } from './items/LineItemList';
import { BalanceDuePanel } from './BalanceDue/BalanceDuePanel';
import Headline from './Headline';
import { formatFractionDigits } from '../../util/fractionDigitsFormatter';
import { OrderDetailsCards } from './orderDetailsCards/OrderDetailsCards';
import {
  pageViewOrderConfirmation,
  pageViewOrderDetails,
} from '../../apis/trackingApi';
import { SourcePageContext } from '../contexts/SourcePageContext';
import useIdentityContext from '../../hooks/useIdentityContext';
import { AnonymousOrderTransferBanner } from './anonymousOrders/AnonymousOrderTransferBanner';
import SmsSubscriptionModal from './smsHandling/SmsSubscriptionModal';
import { EmbeddableLinkCreator } from '../shared/EmbeddableLinkCreator';
import useSiteData from '../../hooks/useSiteData';
import { areAllItemsInTerminalState } from '../../util/mapStateToStatus';

export const OrderDetailsDisplay = () => {
  const { identity } = useIdentityContext();
  const { shopperId, anonymousUserId } = identity;
  const screenClass = useScreenClass();
  const { order } = useContext(OrderDetailsContext);
  const { isConfirmationPage: confirmationMode } =
    useContext(SourcePageContext);
  const fractionDigits = formatFractionDigits(order.fractionalDigits);
  const { externalAppNavigation } = useSiteData();
  const enableTextAlertDialog = useFeature('enableImAlerts');
  const [imAlertData, setImAlertData] = useState({
    skin: undefined,
    alertText: undefined,
  });

  const {
    orderNumber,
    isSmsSubscriptionModalOpen,
    setIsSmsSubscriptionModalOpen,
  } = useContext(OrderDetailsContext);

  const [lineItemMessages] = useTranslations([
    'lineItemMessages',
    'orderSummaryMessages',
  ]);

  const allItemsInTerminalState = areAllItemsInTerminalState(order);
  const orderIsAnonymous = order?.orderPlacedAnonymously;

  const showSmsSubscriptionModal =
    !allItemsInTerminalState && enableTextAlertDialog && !orderIsAnonymous;

  useEffect(() => {
    if (shopperId) {
      if (confirmationMode) {
        pageViewOrderConfirmation(shopperId);
      } else {
        pageViewOrderDetails(shopperId);
      }
    }
    if (anonymousUserId) {
      if (confirmationMode) {
        pageViewOrderConfirmation(anonymousUserId);
      } else {
        pageViewOrderDetails(anonymousUserId);
      }
    }
  }, [confirmationMode, shopperId, anonymousUserId]);

  const updateImAlert = (isErrorAlert, alertMessage) => {
    setImAlertData({
      skin: isErrorAlert ? 'error' : 'success',
      alertText: alertMessage,
    });
  };

  const resetImAlert = () => {
    setImAlertData({ skin: undefined, alertText: undefined });
  };

  const { orderDetails } = externalAppNavigation;
  const OrderDetailsLink = EmbeddableLinkCreator({
    href: `${orderDetails}?orderId=${orderNumber}`,
    tabIndex: 0,
  });

  // setup for link to care
  const navToCustomerCare = externalAppNavigation.customerCareContact;
  const ContactCareLink = EmbeddableLinkCreator({
    href: navToCustomerCare,
    tabIndex: 0,
  });

  return (
    <DetailsOrderUxModWrapper>
      {imAlertData?.alertText && !isSmsSubscriptionModalOpen && (
        <AlertBox mb={8} skin={imAlertData.skin}>
          <AlertBoxDismissButton onClick={() => resetImAlert()} />
          <Typography>
            <ReplaceTokens
              tokenizedString={imAlertData.alertText}
              substitutions={{
                OrderDetailsLink,
                ContactCareLink,
              }}
            />
          </Typography>
        </AlertBox>
      )}
      {showSmsSubscriptionModal && (
        <SmsSubscriptionModal updateAlert={updateImAlert} />
      )}
      <GridContainer>
        <Row
          mt={screenClass === 'xs' ? 7 : 8}
          mb={screenClass === 'xs' ? 6 : 7}
        >
          <Column span={12} className="noprint">
            <AnonymousOrderTransferBanner order={order} />
          </Column>
          <Column span={12}>
            <Headline />
          </Column>
        </Row>
        <Row my={5}>
          <Column span={12}>
            <OrderCondensedSummary
              order={order}
              setTextAlertModalAsOpen={setIsSmsSubscriptionModalOpen}
              displayTextAlertModal={!allItemsInTerminalState}
            />
          </Column>
        </Row>
        <Row>
          <Column span={12} className="noprint">
            <ModTarget name="orderBanner" />
          </Column>
        </Row>
        {order.payments && (
          <Row>
            <Column span={12}>
              <BalanceDuePanel
                payments={order.payments}
                fractionDigits={fractionDigits}
              />
            </Column>
          </Row>
        )}
        <Row
          mt={screenClass === 'xs' ? 7 : 8}
          mb={screenClass === 'xs' ? 0 : 7}
        >
          <Column span={12}>
            <OrderDetailsCards />
          </Column>
        </Row>
        <Row mb={6}>
          <Column span={12} className="noprint">
            <ModTarget name="orderBannerPostSummary" />
          </Column>
        </Row>
        <Row mb={screenClass === 'xs' ? 11 : 12}>
          <Column span={6} mb={5}>
            <Box
              mb={screenClass === 'xs' ? 6 : 7}
              aria-labelledby="items-section-heading"
            >
              <H2 id="items-section-heading">
                {lineItemMessages.titleMessage}
              </H2>
              <LineItemList />
            </Box>
          </Column>
          <Column span={6} mb={5}>
            <Box
              mb={screenClass === 'xs' ? 6 : 7}
              aria-labelledby="order-summary-section-heading"
            >
              <OrderSummary />
            </Box>
            <ModTarget name="belowPricingSummary" />
          </Column>
        </Row>
        <Row mb={6}>
          <Column span={12} className="noprint">
            <ModTarget name="orderBottomBanner" />
          </Column>
        </Row>
      </GridContainer>
    </DetailsOrderUxModWrapper>
  );
};
